import { ClipboardDocumentIcon, EllipsisVerticalIcon, EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { FolderPlusIcon } from '@heroicons/react/24/outline'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useParams } from 'react-router'
import { Head } from '#/components/head'
import { EmptyState } from '#/components/empty-state'
import { Button } from '#/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '#/components/ui/dropdown-menu'
import { Separator } from '#/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#/components/ui/table'
import { Heading, Subheading, Text } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { formatDateTime2Digit } from '#/lib/datetime'
import { keycardAPI } from '#/api/keycard'
import { DeleteKeycardModal } from './@modal/delete/page'
import type { KeycardTemplate } from 'cleverev-types'
import { toast } from 'sonner'


export default function KeycardsPage() {
  const { t, i18n } = useTranslation([ 'keycards' ])
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ keycardId: string } | null>(null)

  // Fetch keycards
  const { data: { keycardTemplates, totalCount } } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'keycards' ],
    queryFn: () => keycardAPI.listKeycards(params.organization),
    select: data => {
      data.keycardTemplates.forEach(keycard => {
        queryClient.setQueryData<KeycardTemplate>(
          [ 'organizations', params.organization, 'keycards', keycard.id ],
          keycard
        )
      })

      return data
    }
  })

  // Copy keycard id and notify
  function copyKeycardId(keycard: KeycardTemplate) {
    copy(keycard.id)
    toast.info(t('view_keycard.id_copied'))
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Head title={t('keycard_list.page_title')} />

      <Outlet />

      <div className="flex w-full items-end justify-between gap-4">
        <Heading>{t('keycard_list.header_title')}</Heading>
        <Link to="./new">
          <Button>
            <PlusIcon />
            {t('keycard_list.action_new_keycard')}
          </Button>
        </Link>
      </div>

      <Text>{t('keycard_list.header_description')}</Text>

      <Separator />

      {totalCount === 0 ? (
        <EmptyState
          title={t('keycard_list.empty_title')}
          description={t('keycard_list.empty_description')}
          Icon={FolderPlusIcon}
        >
          <Link to="./new">
            <Button>
              <PlusIcon />
              {t('keycard_list.action_new_keycard')}
            </Button>
          </Link>
        </EmptyState>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('fields.id')}</TableHead>
                <TableHead>{t('fields.friendlyId')}</TableHead>
                <TableHead>{t('fields.created_at')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isDeleteModalOpen !== null
              && (<DeleteKeycardModal
                id={isDeleteModalOpen.keycardId}
                onClose={() => setIsDeleteModalOpen(null)}
              />)}
              {keycardTemplates.map(keycardData => (
                <TableRow key={keycardData.id}>
                  <TableCell className="font-medium">
                    <Link to={`./${keycardData.id}`} state={{ name: keycardData.id }}>
                      <Subheading>{keycardData.id}</Subheading>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {keycardData.friendlyId ?? '-'}
                  </TableCell>
                  <TableCell>
                    {formatDateTime2Digit(keycardData.createdAt, i18n.language)}
                  </TableCell>
                  <TableCell className="text-right flex flex-row justify-end">
                    <Link to={`./${keycardData.id}`} state={{ name: keycardData.id }}>
                      <Button variant="ghost" className="mr-4">
                        {t('keycard_list.view_button')}
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <EllipsisVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuLabel>{t('keycard_list.menu.label')}</DropdownMenuLabel>
                        <Link to={`./${keycardData.id}`} state={{ name: keycardData.id }}>
                          <DropdownMenuItem>
                            <EyeIcon className="text-tertiary" />
                            {t('keycard_list.menu.view')}
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuItem onClick={() => copyKeycardId(keycardData)}>
                          <ClipboardDocumentIcon className="text-tertiary" />
                          {t('keycard_list.menu.copy_id')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() =>
                          setIsDeleteModalOpen({
                            keycardId: keycardData.id
                          })}>
                          <TrashIcon className="text-tertiary" />
                          {t('keycard_list.menu.remove')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

    </div>
  )
}
